import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import DialogEditUser from "src/views/dialog/DialogEditUser";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import TableCategories from "src/views/tables/TableCategories";
import DialogUserStatusUpdate from "src/views/dialog/DialogUserStatusUpdate";
import DialogAddCategory from "src/views/dialog/DialogAddCategory";

const CategoryPage = () => {
    const searchTimeoutRef = useRef();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [search, setSearch] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(
        DefaultPaginationSettings.ROWS_PER_PAGE
    );
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState(null);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [dialogMode, setDialogMode] = useState("add");
    const [confirmOpen, setConfirmOpen] = useState(false);

    const toggleConfirm = (e, category = null) => {
        setConfirmOpen((prev) => !prev);
        setCategoryToDelete(category);
    };

    // Function to open status dialog
    const handleStatusDialogOpen = (category) => {
            setSelectedCategory(category);
        setStatusDialogOpen(true);
    };

    // Function to close status dialog
    const handleStatusDialogClose = () => {
        setSelectedCategory(null);
        setStatusDialogOpen(false);
    };

    // Function to open category dialog for add
    const handleAddCategory = () => {
        setCategoryToEdit(null);
        setDialogMode("add");
        setCategoryDialogOpen(true);
    };

    // Function to open category dialog for edit
    const handleEditCategory = (e,category) => {
        console.log("category", category);
        setCategoryToEdit(category);
        setDialogMode("edit");
        setCategoryDialogOpen(true);
    };

    // Function to close category dialog
    const handleCategoryDialogClose = () => {
        setCategoryDialogOpen(false);
        setCategoryToEdit(null);
        setDialogMode("add");
    };

    const fetchData = ({ currentPage, pageSize, search }) => {
        setLoading(true);
        const params = { page: currentPage, limit: pageSize, search };

        axiosInstance
            .get(ApiEndPoints.CATEGORIES?.list, { params })
            .then((response) => {
                setCategories(response.data?.data?.categories || []);
                setTotalCount(response.data?.data?.totalRecords || 0);
            })
            .catch((error) => toastError(error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData({ currentPage, pageSize, search });
    }, [currentPage, pageSize, search]);

    const handleSearchChange = (e) => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        searchTimeoutRef.current = setTimeout(() => {
            setSearch(e.target.value);
            setCurrentPage(1);
        }, 500);
    };

    const onConfirmDelete = () => {
        axiosInstance
            .delete(ApiEndPoints.CATEGORIES.delete(categoryToDelete?.id))
            .then((response) => {
                toastSuccess(response.data?.message || "Category deleted successfully");
                fetchData({ currentPage, pageSize, search });
                setConfirmOpen(false);
                setCategoryToDelete(null);
            })
            .catch((error) => {
                toastError(error);
            });
    };

    return (
        <>
            <Grid container spacing={4} padding={2}>
                <Grid item xs={12}>
                    <Box sx={{
                        p: 5,
                        mb: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <PageHeader
                            title={
                                <Typography variant="h5">
                                    <Translations text="Category" />
                                </Typography>
                            }
                            action={
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddCategory}
                                >
                                    Add Category
                                </Button>
                            }
                        />
                    </Box>
                    <Card>
                        <Box
                            sx={{
                                p: 5,
                                pb: 0, display: "flex", alignItems: "center",justifyContent:"flex-end"
                            }} >
                            <TextField
                                type="search"
                                size="small"
                                placeholder="Search"
                                onChange={handleSearchChange}
                            />
                        </Box>
                        <Box sx={{ p: 5 }}>
                            <TableCategories
                                rows={categories}
                                loading={loading}
                                totalCount={totalCount}
                                setCurrentPage={setCurrentPage}
                                currentPage={currentPage}
                                setPageSize={setPageSize}
                                pageSize={pageSize}
                                toggleEdit={handleEditCategory}
                                toggleStatus={handleStatusDialogOpen}
                                toggleDelete={toggleConfirm}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            <DialogAddCategory
                open={categoryDialogOpen}
                onClose={handleCategoryDialogClose}
                onSuccess={() => {
                    fetchData({
                        currentPage: currentPage,
                        pageSize: pageSize,
                        search: search,
                    });
                }}
                mode={dialogMode}
                dataToEdit={categoryToEdit}
            />

            <DialogUserStatusUpdate
                open={statusDialogOpen}
                toggle={handleStatusDialogClose}
                dataToEdit={selectedCategory}
                endpointType="CATEGORIES"
                onSuccess={() => {
                    fetchData({
                        currentPage: currentPage,
                        pageSize: pageSize,
                        search: search,
                    });
                }}
            />
            <DialogConfirmation
                open={confirmOpen}
                toggle={toggleConfirm}
                title="Delete Category"
                subtitle="Are you sure you want to delete this category?"
                onConfirm={onConfirmDelete}
            />
        </>
    );
};

export default CategoryPage;
